export type SpinPhase = 'idle' | 'accelerating' | 'constant' | 'decelerating' | 'bouncing' | 'stopped'

export interface SpinAnimatorConfig {
  reelCount: number
  symbolHeight: number
  spinDuration?: number // base duration in ms per reel, default 2000
  staggerDelay?: number // additional ms per reel index for stop stagger, default 150
  bounceOvershoot?: number // overshoot as fraction of symbolHeight, default 0.1
}

interface ReelAnimState {
  phase: SpinPhase
  elapsed: number // total elapsed time since spin start (ms)
  duration: number // total animation duration for this reel (ms)
  targetOffset: number // target y-offset in pixels
  currentOffset: number // current y-offset in pixels
}

// Phase boundaries as fraction of each reel's total duration
const PHASE_ACCEL_END = 0.33
const PHASE_CONSTANT_END = 0.66
const PHASE_DECEL_END = 0.95

/**
 * SpinAnimator handles the timing and easing of slot reel spin animations.
 *
 * Each reel goes through 4 phases:
 *   1. Acceleration (0–33%): ease-in from rest to max speed
 *   2. Constant (33–66%): steady speed
 *   3. Deceleration (66–95%): ease-out to target position
 *   4. Bounce (95–100%): overshoot target, spring back to exact position
 *
 * Reels stop in sequence (left-to-right stagger):
 *   reel 0 duration = spinDuration
 *   reel N duration = spinDuration + N × staggerDelay
 */
export class SpinAnimator {
  private readonly _reelCount: number
  private readonly _symbolHeight: number
  private readonly _spinDuration: number
  private readonly _staggerDelay: number
  private readonly _bounceOvershoot: number

  private reelStates: ReelAnimState[]
  private spinning: boolean = false
  private completeCallback: (() => void) | null = null

  constructor(config: SpinAnimatorConfig) {
    this._reelCount = config.reelCount
    this._symbolHeight = config.symbolHeight
    this._spinDuration = config.spinDuration ?? 2000
    this._staggerDelay = config.staggerDelay ?? 150
    this._bounceOvershoot = config.bounceOvershoot ?? 0.1

    this.reelStates = []
    for (let i = 0; i < this._reelCount; i++) {
      this.reelStates.push(this.createIdleState())
    }
  }

  private createIdleState(): ReelAnimState {
    return {
      phase: 'idle',
      elapsed: 0,
      duration: 0,
      targetOffset: 0,
      currentOffset: 0,
    }
  }

  get isSpinning(): boolean {
    return this.spinning
  }

  get reelCount(): number {
    return this._reelCount
  }

  /**
   * Start spin animation for all reels.
   * @param targetPositions scroll distance per reel in symbol units
   * @param onComplete fires when every reel has stopped
   */
  startSpin(targetPositions: number[], onComplete?: () => void): void {
    this.spinning = true
    this.completeCallback = onComplete ?? null

    for (let i = 0; i < this._reelCount; i++) {
      const target = (targetPositions[i] ?? 0) * this._symbolHeight
      const duration = this._spinDuration + i * this._staggerDelay

      this.reelStates[i] = {
        phase: 'accelerating',
        elapsed: 0,
        duration,
        targetOffset: target,
        currentOffset: 0,
      }
    }
  }

  /**
   * Advance the animation by deltaMs milliseconds.
   * @returns current y-offset for each reel in pixels
   */
  update(deltaMs: number): number[] {
    if (!this.spinning) {
      return this.reelStates.map((s) => s.currentOffset)
    }

    for (let i = 0; i < this._reelCount; i++) {
      const state = this.reelStates[i]
      if (state.phase === 'stopped' || state.phase === 'idle') continue

      state.elapsed += deltaMs

      const progress = Math.min(state.elapsed / state.duration, 1.0)

      if (progress >= 1.0) {
        state.phase = 'stopped'
        state.currentOffset = state.targetOffset
      } else if (progress >= PHASE_DECEL_END) {
        state.phase = 'bouncing'
        state.currentOffset = this.getBouncePosition(state.targetOffset, progress)
      } else {
        // Phases 1-3 share the same base position curve
        if (progress < PHASE_ACCEL_END) {
          state.phase = 'accelerating'
        } else if (progress < PHASE_CONSTANT_END) {
          state.phase = 'constant'
        } else {
          state.phase = 'decelerating'
        }
        state.currentOffset = this.getBasePosition(state.targetOffset, progress)
      }
    }

    // Check completion after processing all reels
    const allDone = this.reelStates.every((s) => s.phase === 'stopped' || s.phase === 'idle')
    if (allDone) {
      this.spinning = false
      this.completeCallback?.()
    }

    return this.reelStates.map((s) => s.currentOffset)
  }

  /**
   * Position for phases 1–3 (acceleration → constant → deceleration).
   * Maps progress [0, 0.95] to position [0, target] using a velocity-area model:
   *   Phase 1: velocity ramps 0→V (quadratic ease-in)
   *   Phase 2: velocity = V (linear distance)
   *   Phase 3: velocity ramps V→0 (quadratic ease-out)
   */
  private getBasePosition(target: number, progress: number): number {
    // Normalize progress into the 0–0.95 range → 0–1
    const p = Math.min(progress / PHASE_DECEL_END, 1.0)

    // Normalized phase boundaries
    const P1 = PHASE_ACCEL_END / PHASE_DECEL_END
    const P2 = PHASE_CONSTANT_END / PHASE_DECEL_END

    const d1 = P1
    const d2 = P2 - P1
    const d3 = 1.0 - P2

    // Total area under the velocity curve (integral = 1 when fully traversed)
    const totalArea = d1 / 2 + d2 + d3 / 2

    let area: number

    if (p <= P1) {
      // Phase 1: Acceleration (quadratic ease-in)
      const t = p / d1
      area = (d1 * t * t) / 2
    } else if (p <= P2) {
      // Phase 2: Constant speed (linear)
      const t = (p - P1) / d2
      area = d1 / 2 + d2 * t
    } else {
      // Phase 3: Deceleration (quadratic ease-out)
      const t = (p - P2) / d3
      area = d1 / 2 + d2 + d3 * (t - (t * t) / 2)
    }

    return target * (area / totalArea)
  }

  /**
   * Position during bounce phase (progress 0.95 → 1.0).
   * Overshoots target by bounceOvershoot × symbolHeight, then springs back.
   */
  private getBouncePosition(target: number, progress: number): number {
    const bounceProgress = (progress - PHASE_DECEL_END) / (1.0 - PHASE_DECEL_END)
    const overshootPx = this._bounceOvershoot * this._symbolHeight
    return target + Math.sin(bounceProgress * Math.PI) * overshootPx
  }

  /** Current animation phase for a specific reel */
  getReelPhase(reelIndex: number): SpinPhase {
    return this.reelStates[reelIndex]?.phase ?? 'idle'
  }

  /** Current y-offset for a specific reel in pixels */
  getReelOffset(reelIndex: number): number {
    return this.reelStates[reelIndex]?.currentOffset ?? 0
  }

  /** Total spin duration for a specific reel including stagger (ms) */
  getReelDuration(reelIndex: number): number {
    return this._spinDuration + reelIndex * this._staggerDelay
  }

  /** Configured stagger delay between consecutive reel stops (ms) */
  get staggerDelay(): number {
    return this._staggerDelay
  }

  /** Configured bounce overshoot fraction */
  get bounceOvershoot(): number {
    return this._bounceOvershoot
  }

  /** Configured symbol height in pixels */
  get symbolHeight(): number {
    return this._symbolHeight
  }

  /** Reset all reels to idle state */
  reset(): void {
    this.spinning = false
    this.completeCallback = null
    for (let i = 0; i < this._reelCount; i++) {
      this.reelStates[i] = this.createIdleState()
    }
  }
}
